// Client for the R2 connect flow (the publishing worker). OAuth providers open
// a popup to the worker's start, consent and callback flow, which postMessages
// the result back.

/** `added` is false when the network returned an account that was already connected. */
export interface ConnectResult {
  ok: boolean
  error?: string
  accountId?: string
  added?: boolean
}

function connectedResult(data: { accountId?: unknown; account?: { id?: unknown }; added?: unknown }): ConnectResult {
  const id = data.accountId ?? data.account?.id
  return {
    ok: true,
    accountId: typeof id === 'string' ? id : undefined,
    added: typeof data.added === 'boolean' ? data.added : undefined,
  }
}

export interface ProviderInfo {
  provider: string
  kind: 'oauth2' | 'app_password' | 'instance_oauth'
  charLimit: number
  credentialFree: boolean
  configured: boolean
}

// Worker auth uses the Clerk session token (verified server-side by @clerk/backend).
async function authHeader(): Promise<Record<string, string>> {
  const clerk = (window as unknown as { Clerk?: { session?: { getToken: () => Promise<string | null> } } }).Clerk
  const token = clerk?.session ? await clerk.session.getToken() : null
  return token ? { authorization: `Bearer ${token}` } : {}
}

export async function fetchProviders(): Promise<ProviderInfo[]> {
  try {
    const res = await fetch((import.meta.env.VITE_API_BASE || '') + '/api/connect/providers', { headers: await authHeader() })
    if (!res.ok) return []
    const data = await res.json()
    return data.providers ?? []
  } catch {
    return []
  }
}

/** Connect Bluesky with a handle + app password (no popup, no dev app). */
export async function connectBluesky(opts: {
  identifier: string
  appPassword: string
  service?: string
}): Promise<ConnectResult> {
  try {
    const res = await fetch((import.meta.env.VITE_API_BASE || '') + '/api/connect/bluesky', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(await authHeader()) },
      body: JSON.stringify(opts),
    })
    const data = await res.json().catch(() => ({}))
    return res.ok ? connectedResult(data) : { ok: false, error: data.error || data.message || 'connect_failed' }
  } catch (e: any) {
    return { ok: false, error: e?.message || 'network_error' }
  }
}

/**
 * Start a redirect-based connect (LinkedIn, X, Mastodon). Opens a popup and
 * resolves when the worker's callback posts back. `host` is required for Mastodon.
 */
export async function connectOAuth(
  provider: string,
  opts: { host?: string } = {},
): Promise<ConnectResult> {
  // Open the popup SYNCHRONOUSLY on the user gesture. Opening it after the
  // `await fetch` below loses user-activation and the browser blocks the popup.
  const popup = window.open('about:blank', 'heytasky-connect', 'width=640,height=760')
  if (!popup) return { ok: false, error: 'popup_blocked' }

  const qs = opts.host ? `?host=${encodeURIComponent(opts.host)}` : ''
  const apiBase = import.meta.env.VITE_API_BASE || ''
  let url: string
  // The only window allowed to report a result is the worker's callback page.
  // The worker names its callback origin at /start; fall back to the API base.
  let callbackOrigin = apiBase ? new URL(apiBase, window.location.origin).origin : window.location.origin
  try {
    const res = await fetch((import.meta.env.VITE_API_BASE || '') + `/api/connect/${provider}/start${qs}`, { headers: await authHeader() })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.url) {
      popup.close()
      return { ok: false, error: data.message || data.error || 'start_failed' }
    }
    url = data.url
    if (typeof data.callbackOrigin === 'string' && /^https?:\/\//.test(data.callbackOrigin)) {
      callbackOrigin = data.callbackOrigin
    }
  } catch (e: any) {
    popup.close()
    return { ok: false, error: e?.message || 'network_error' }
  }

  popup.location.href = url

  return new Promise((resolve) => {

    const onMessage = (ev: MessageEvent) => {
      if (ev.origin !== callbackOrigin) return
      if (ev.data?.source !== 'heytasky-connect') return
      cleanup()
      resolve(ev.data.ok ? connectedResult(ev.data) : { ok: false, error: ev.data.error || 'connect_failed' })
    }
    const timer = window.setInterval(() => {
      if (popup.closed) {
        cleanup()
        resolve({ ok: false, error: 'cancelled' })
      }
    }, 800)
    function cleanup() {
      window.clearInterval(timer)
      window.removeEventListener('message', onMessage)
      try {
        popup?.close()
      } catch {
        /* ignore */
      }
    }
    window.addEventListener('message', onMessage)
  })
}
